
import React, { useState, useEffect } from 'react';
import { useAppContext } from '../App';
import { Bell, Shield, Globe, BookOpen, Lock, Check, Save, AlertTriangle, Loader2 } from 'lucide-react';
import { BibleVersion } from '../types';

const SettingsPage: React.FC = () => {
    const { currentUser, setLang, lang, updateUser, t } = useAppContext();
    
    // Local state for form management
    const [selectedLang, setSelectedLang] = useState(lang);
    const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
        return localStorage.getItem('holyness_notifications') === 'false' ? false : true;
    });
    const [bibleVersion, setBibleVersion] = useState<BibleVersion>(() => {
        return (localStorage.getItem('holyness_bible_version') as BibleVersion) || BibleVersion.NIV;
    });
    
    const [successMsg, setSuccessMsg] = useState<string | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    // Sync state with context on load
    useEffect(() => {
        if(currentUser?.language) setSelectedLang(currentUser.language);
    }, [currentUser]);

    const handleSave = async () => {
        if(!currentUser) return;
        setSuccessMsg(null);
        setErrorMsg(null);
        setIsSaving(true);

        try {
            // 1. Save App-Level Settings (Local Storage)
            localStorage.setItem('holyness_notifications', notificationsEnabled.toString());
            localStorage.setItem('holyness_bible_version', bibleVersion);
            
            // 2. Update Context Language
            setLang(selectedLang);

            // 3. Persist User Profile Settings to Database
            // The updateUser function in App.tsx now handles error logging
            await updateUser({
                ...currentUser,
                language: selectedLang
            });

            setSuccessMsg(t('save_prefs') + " successfully!");
            setTimeout(() => setSuccessMsg(null), 3000);

        } catch (error: any) {
            console.error("Failed to save settings", error);
            setErrorMsg("Failed to save changes. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    if (!currentUser) return null;

    return (
        <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('settings_title')}</h1>
            
            {successMsg && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
                    <Check className="w-5 h-5" /> {successMsg}
                </div>
            )}
             {errorMsg && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" /> {errorMsg}
                </div>
            )}

            {/* Account Settings */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <Shield className="w-5 h-5 text-gray-500" /> {t('account_security')}
                    </h2>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth_email')}</label>
                            <input 
                                type="email" 
                                disabled 
                                value={currentUser.email} 
                                className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 text-gray-500 cursor-not-allowed"
                            />
                            <p className="text-xs text-gray-500 mt-1">Managed via Login Provider</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth_password')}</label>
                            <button className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium border border-blue-200 bg-blue-50 px-4 py-2 rounded-lg transition hover:bg-blue-100">
                                <Lock className="w-4 h-4" /> {t('change_password')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Faith Preferences */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-gray-500" /> {t('faith_prefs')}
                    </h2>
                </div>
                <div className="p-6 space-y-4">
                     <div className="max-w-md">
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('pref_bible_version')}</label>
                        <select 
                            value={bibleVersion}
                            onChange={(e) => setBibleVersion(e.target.value as BibleVersion)}
                            className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors"
                        >
                            {Object.values(BibleVersion).map(v => (
                                <option key={v} value={v}>{v}</option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">This version will be used for daily verses and sermon generation.</p>
                     </div>
                </div>
            </div>

            {/* Application Settings */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <Globe className="w-5 h-5 text-gray-500" /> {t('application')}
                    </h2>
                </div>
                <div className="p-6 space-y-4">
                     <div className="flex items-center justify-between py-2 border-b border-gray-100">
                        <div>
                            <p className="font-medium text-gray-900">{t('language')}</p>
                            <p className="text-sm text-gray-500">Select your preferred interface language.</p>
                        </div>
                        <select 
                            value={selectedLang} 
                            onChange={(e) => setSelectedLang(e.target.value)}
                            className="bg-transparent border-b border-gray-300 rounded-none px-2 py-1 focus:outline-none focus:border-blue-600 transition-colors"
                        >
                            <option value="en">English</option>
                            <option value="es">Español</option>
                            <option value="pt">Português</option>
                            <option value="fr">Français</option>
                        </select>
                     </div>

                     <div className="flex items-center justify-between py-2">
                        <div>
                            <p className="font-medium text-gray-900 flex items-center gap-2"><Bell className="w-4 h-4"/> {t('enable_notifications')}</p>
                            <p className="text-sm text-gray-500">Receive updates about church events and prayer requests.</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                                type="checkbox" 
                                checked={notificationsEnabled}
                                onChange={(e) => setNotificationsEnabled(e.target.checked)}
                                className="sr-only peer" 
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                     </div>
                </div>
            </div>

            <div className="flex justify-end pt-4 pb-12">
                <button 
                    onClick={handleSave}
                    disabled={isSaving}
                    className="bg-blue-600 text-white px-8 py-3 rounded-lg font-bold shadow-lg hover:bg-blue-700 transition flex items-center gap-2 disabled:opacity-50"
                >
                    {isSaving ? (
                        <><Loader2 className="w-4 h-4 animate-spin"/> {t('save')}...</>
                    ) : (
                        <><Save className="w-4 h-4" /> {t('save_prefs')}</>
                    )}
                </button>
            </div>
        </div>
    );
};

export default SettingsPage;
